import React, { useCallback, useEffect } from 'react'

import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import Button from '@/components/common/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import FileUpload, { CompactLocalFile } from '@/components/files/FileUpload'
import Label from '@/components/form/Label'
import { getPersonFullName } from '@/components/library-v2/people/lib'
import { BasicBlueButton } from '@/components/library-v2/ui/Button'
import { FileCombobox } from '@/components/library-v2/ui/Combobox'

import { useUpdatePerson } from '@/hooks/library/useUpdatePerson'
import { useUploadResume } from '@/hooks/library/useUploadResume'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganizationFiles } from '@/hooks/organization/useOrganizationFiles'

import type { UUID } from '@/types/Id'
import type { EntityPerson } from '@/types/library/Entity'

interface AddResumeDialogProps {
  person: EntityPerson
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface AddResumeForm {
  resume_file_id: UUID | null
  files: File[]
}

export const AddResumeDialog: React.FC<AddResumeDialogProps> = ({
  person,
  open,
  onOpenChange,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: files } = useOrganizationFiles({ orgId: activeOrgId })

  const { mutateAsync: updatePerson, isPending: isUpdatingPerson } =
    useUpdatePerson({
      orgId: activeOrgId,
      optimisticUpdate: false,
      invalidateOnSuccess: true,
    })

  const { mutateAsync: uploadResume, isPending: isUploading } = useUploadResume(
    {
      orgId: activeOrgId,
      personId: person.id as UUID,
    },
  )

  const isSaving = isUploading || isUpdatingPerson

  const { watch, setValue, reset } = useForm<AddResumeForm>({
    defaultValues: { resume_file_id: null, files: [] },
  })
  const resumeFileId = watch('resume_file_id')
  const localFiles = watch('files')

  const personName = getPersonFullName(person) ?? 'this person'

  const handleClose = useCallback(() => {
    onOpenChange(false)
  }, [onOpenChange])

  const handleSave = useCallback(async () => {
    try {
      if (localFiles.length > 0) {
        await uploadResume(localFiles[0], {
          onSuccess: () => {
            onOpenChange(false)
            toast.success('Successfully uploaded resume!')
          },
        })
      } else if (resumeFileId) {
        await updatePerson(
          { ...person, resume_file_id: resumeFileId },
          {
            onSuccess: () => {
              onOpenChange(false)
              toast.success('Successfully added resume!')
            },
          },
        )
      }
    } catch (e) {
      console.error(e)
    }
  }, [
    localFiles,
    resumeFileId,
    person,
    updatePerson,
    uploadResume,
    onOpenChange,
  ])

  const handleResetResume = useCallback(() => {
    setValue('resume_file_id', null)
  }, [setValue])

  useEffect(() => {
    if (open) {
      reset({ resume_file_id: null, files: [] })
    }
  }, [open, person?.id, reset])

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          aria-describedby={undefined}
          className="sm:min-w-[600px] p-5"
        >
          <DialogHeader>
            <DialogTitle>Add Resume</DialogTitle>
            <DialogDescription>
              Add a resume to help Sweetspot AI better understand{' '}
              <span className="font-medium">{personName}</span>&apos;
              {personName.endsWith('s') ? '' : 's'} skills and experience.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-6 mt-6 mb-4">
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between h-3.5">
                <Label>Pick from existing files</Label>
                {resumeFileId ? (
                  <BasicBlueButton
                    type="button"
                    onClick={handleResetResume}
                    className="text-sm"
                  >
                    Reset
                  </BasicBlueButton>
                ) : null}
              </div>
              <FileCombobox
                value={resumeFileId}
                files={files}
                onChange={(v) => setValue('resume_file_id', v)}
                disabled={localFiles.length > 0}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label>Or upload a new resume</Label>
              <FileUpload
                onAddFiles={(fs) => setValue('files', fs.slice(0, 1))}
                maxFiles={1}
                accepts={[
                  'application/pdf',
                  'application/rtf',
                  'application/vnd.oasis.opendocument.text',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
                  'application/msword',
                ]}
                className="dark:bg-default-dark dark:hover:bg-primary-900 h-32"
                disabled={Boolean(resumeFileId)}
              />
              {localFiles.length > 0 && (
                <div className="pt-1">
                  <CompactLocalFile
                    file={localFiles[0]}
                    onDelete={() => setValue('files', [])}
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter className="justify-end pt-1">
            <Button variant="outline-compact" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary-compact"
              onClick={handleSave}
              disabled={(!resumeFileId && localFiles.length === 0) || isSaving}
              isLoading={isSaving}
            >
              Add Resume
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
